import { docsLastPage, isSectionNav, owningSectionId, sectionTarget } from 'BomdisoftComponents/docs-shell/sections';
import type { DocsNavGroup, DocsNavSection } from 'BomdisoftComponents/docs-shell/types';
import { delegateTabOverflow } from 'BomdisoftComponents/tab/delegate';
import { delegateTooltips } from 'BomdisoftComponents/tooltip/delegate';

export type DocsShellBehaviour = {
  langPrefix: string;
  nav: DocsNavGroup[] | DocsNavSection[];
  onNavigate: (href: string) => void;
  pathname: string;
};

export const delegateDocsShell = (root: HTMLElement, { langPrefix, nav, onNavigate, pathname }: DocsShellBehaviour) => {
  const view = root.ownerDocument.defaultView;
  const drawer = root.querySelector<HTMLDialogElement>('dialog.bomdisoft-docs-shell-drawer');
  if (view === null || drawer === null) return () => {};
  const tooltipRoot = root.getRootNode();
  if (tooltipRoot.nodeType === tooltipRoot.DOCUMENT_NODE || tooltipRoot.nodeType === tooltipRoot.DOCUMENT_FRAGMENT_NODE) delegateTooltips(tooltipRoot as Document | ShadowRoot);
  const sections = isSectionNav(nav) ? nav : null;
  const owner = sections === null ? undefined : owningSectionId(sections, pathname, langPrefix);
  if (owner !== undefined) docsLastPage.write(pathname, owner);
  const overflows = [...root.querySelectorAll<HTMLElement>('.bomdisoft-docs-shell-segmented-frame')].map(frame => delegateTabOverflow(frame));

  const closeDrawer = () => {
    if (drawer.open) drawer.close();
  };

  const openSection = (testId: string) => {
    const section = sections?.find(candidate => candidate.testId === testId);
    if (section === undefined) return;
    const target = sectionTarget(section, docsLastPage.read(section.id), langPrefix);
    if (target === null || target.replace(/\/+$/, '') === pathname.replace(/\/+$/, '')) return;
    closeDrawer();
    onNavigate(target);
  };

  const handleClick = (event: MouseEvent) => {
    const target = event.target instanceof view.Element ? event.target : null;
    if (target === null) return;
    if (target === drawer) {
      closeDrawer();
      return;
    }
    if (target.closest('[data-testid="docs-menu-button"]') !== null) {
      drawer.showModal();
      drawer.focus();
      return;
    }
    const segment = target.closest('.bomdisoft-docs-shell-segment');
    if (segment !== null) {
      openSection(segment.getAttribute('data-testid') ?? '');
      return;
    }
    if (target.closest('.bomdisoft-docs-shell-item') !== null && drawer.contains(target)) closeDrawer();
  };

  const tocItems = [...root.querySelectorAll<HTMLAnchorElement>('.bomdisoft-docs-shell-toc-item')];
  const markActive = (id: string) => {
    for (const item of tocItems) {
      if (item.getAttribute('href') === `#${id}`) item.setAttribute('data-active', 'true');
      else item.removeAttribute('data-active');
    }
  };
  const headings = tocItems.map(item => root.ownerDocument.getElementById(item.getAttribute('href')?.slice(1) ?? '')).filter((heading): heading is HTMLElement => heading !== null);
  const observer =
    headings.length === 0 || typeof view.IntersectionObserver !== 'function'
      ? null
      : new view.IntersectionObserver(
          entries => {
            const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (visible[0]) markActive(visible[0].target.id);
          },
          { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
        );
  for (const heading of headings) observer?.observe(heading);

  root.addEventListener('click', handleClick);
  return () => {
    observer?.disconnect();
    for (const overflow of overflows) overflow.release();
    root.removeEventListener('click', handleClick);
  };
};
